"use client";

import {useRef} from "react";
import {PagePayoutSchema} from "@/sanity/pages/PagePayoutTemplate";

import {cn} from "@/lib/utils";
import Image from "next/image";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import CryptoPillar from "@/components/global/ui/CryptoPillar";
import {CTAButton} from "@/shadcn/ui/button";
import {useScroll, useTransform} from "framer-motion";
import ChipSerif from "@/components/global/ui/ChipSerif";

import bg from "@/public/global/bg-art.jpg";
import statue from "@/public/payout/statue.png";

export default function HeroSection({
  data,
  className,
}: {
  data: PagePayoutSchema;
  className?: string;
}) {
  const parallaxRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: parallaxRef,
    offset: ["start end", "end start"],
  });

  const yEthereum = useTransform(scrollYProgress, [0, 1], ["-3%", "3%"]);
  const ySolana = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const yBitcoin = useTransform(scrollYProgress, [0, 1], ["5%", "-5%"]);

  return (
    <section
      id="hero-section"
      ref={parallaxRef}
      className={cn(
        "relative overflow-clip px-[2rem] lg:px-[5rem] pt-[2rem] lg:pt-[5rem]",
        className,
      )}
    >
      <div className="relative overflow-clip w-[min(229.25rem,100%)] mx-auto rounded-[2rem] lg:rounded-[5rem] pt-[14rem] lg:pt-[25rem] pb-0 lg:pb-[4.5rem] min-h-[72.875rem] flex flex-col items-center">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-[#F6F9F7] to-[#E6F2EB]">
          <Image
            className="absolute left-1/2 -translate-x-1/2 bottom-0 !w-[max(100%,90rem)] !max-w-none !h-full object-cover object-top mix-blend-luminosity opacity-[.36]"
            src={bg}
            quality={100}
            alt="background art"
          />
        </div>

        <div className="container relative z-10 flex flex-col items-center">
          <div className="w-[min(179.5rem,100%)] flex flex-col items-center lg:items-start">
            <ChipSerif text={data?.heroChip} />

            {data?.heroText && (
              <div className="richtext lg:richtext-start prose-p:w-[min(46.75rem,100%)]">
                <PortableText
                  value={data.heroText}
                  components={richTextComponent}
                />
              </div>
            )}

            {data?.heroCTA && (
              <CTAButton
                className="mt-[3rem]"
                data={data.heroCTA}
              />
            )}
          </div>
        </div>

        <CryptoPillar
          className="left-[calc(50%-21rem)] lg:left-[calc(50%+17rem)] h-[25rem] lg:!scale-100"
          type="ethereum"
          style={{ y: yEthereum }}
        />
        <CryptoPillar
          className="bottom-[5rem] lg:bottom-0 left-[calc(50%-9rem)] lg:left-[calc(50%+36rem)] h-[40rem] lg:h-[52rem] lg:!scale-[1.16]"
          type="solana"
          style={{ y: ySolana }}
        />
        <CryptoPillar
          className="bottom-[11rem] lg:bottom-0 left-[calc(50%+18rem)] lg:left-[calc(50%+77rem)] h-[17rem] lg:h-[30rem] lg:!scale-[1.08]"
          type="bitcoin"
          style={{ y: yBitcoin }}
        />

        <div className="w-[min(51.375rem,75%)] bottom-0 lg:-bottom-[5rem] lg:left-[calc(50%+22rem)] -translate-x-[2rem] lg:translate-x-0 relative lg:absolute z-10 mt-[4.5rem] lg:mt-0">
          <Image
            className="!w-full"
            src={statue}
            quality={100}
            alt="statue"
          />
        </div>
      </div>
    </section>
  );
}
